import { samePageScope } from '../../../../composables/samePageScope'
import { computed, type ComputedRef } from 'vue'
import { z } from 'zod'
import { ApiError, type ApiClient } from '@fusapp-work/api'
import { useApiClient, useQuery } from '@fusapp-work/shell'
import { useCurrentSession } from '../../../../composables/useCurrentSession'
import { parseResponse } from '../../../organization/shared/response'
import { channelTypes } from '../../channels/domain/channels'
import { dotnetTrim, hasControlChar, isMailAddress, serverText } from '../../channels/domain/mailAddress'

// Int64 wire counts fail closed above the exact JavaScript integer boundary.
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const pageNumber = z.number().int().min(1).max(10000)
const pageSize = z.number().int().min(1).max(100)
const namedId = z.string().refine(value => value.trim().length > 0)
export const templateIdSchema = z.string().regex(/^[a-f\d]{24}$/i)
const revisionSchema = z.string().regex(/^[a-f\d]{64}$/)
// Server-owned trigger identifier; only a copy may carry a trigger other than manual send (the server decides which).
const triggerNameSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,127}$/)
export const templateCreateSchema = z.object({
  code: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).min(2).max(64),
  name: z.string().transform(serverText).pipe(z.string().min(1).max(255).refine(value => !hasControlChar(value))),
  channelType: z.enum(['Email', 'SMS', 'PushNotification']),
  copyFromTemplateId: templateIdSchema.optional(), trigger: triggerNameSchema,
}).refine(value => value.trigger === 'ManualSendRequest' || value.copyFromTemplateId !== undefined)
export type TemplateCreateInput = z.input<typeof templateCreateSchema>
const templateCreatedSchema = z.object({ id: templateIdSchema })
const contentCreatedSchema = z.object({ templateId: templateIdSchema, contentId: templateIdSchema })
const templateStatusResultSchema = z.object({ templateId: templateIdSchema, contentId: z.null() })
const contentCreateInputSchema = z.object({ channelType: z.enum(['Email', 'SMS', 'PushNotification']), language: z.string().trim().min(2).max(35) })
const revisionInputSchema = z.object({ expectedRevision: revisionSchema })
const contentStatusInputSchema = revisionInputSchema.extend({ isEnabled: z.boolean() })
const templateStatusInputSchema = z.object({ isEnabled: z.boolean() })
// Lifecycle validation checks controls on the original reason, before trimming its length/persisted value.
export const deleteTemplateInputSchema = z.object({ reason: z.string().refine(value => !hasControlChar(value))
  .transform(serverText).pipe(z.string().min(1).max(255)) })
// Backend ValidRecipient: 3–254 characters, untrimmed, no char.IsControl, and exactly what MailAddress parses.
export const templateTestSendInputSchema = z.object({ contentId: templateIdSchema,
  recipientAddress: z.string().min(3).max(254).refine(value => value === dotnetTrim(value) && !hasControlChar(value) && isMailAddress(value)),
  sampleValues: z.record(z.string().regex(/^[a-z][a-z0-9]*(?:\.[a-z][a-z0-9]*)+$/), z.string()).optional() })
export const templateTestKeySchema = z.uuid().transform(value => value.toLowerCase())
const testSendStatusSchema = z.object({ trackingId: z.string().regex(/^console-template-test:[a-f\d]{32}$/i), state: z.enum(['pending', 'accepted', 'uncertain']) })
const testSendPostResultSchema = testSendStatusSchema.extend({ state: z.enum(['pending', 'accepted']) })
const templateContentSchema = z.object({
  id: templateIdSchema, language: z.string().nullable(), isDefault: z.boolean().nullable(), isEnabled: z.boolean().nullable(),
  subject: z.string().nullable(), body: z.string().nullable(), revision: revisionSchema.nullable(),
  supportedChannelTypes: z.enum(channelTypes).array().nullable(),
})
const allowedVariableSchema = z.object({ key: z.string().regex(/^[a-z][a-z0-9]*(?:\.[a-z][a-z0-9]*)+$/), sample: z.string(), scope: z.enum(['trigger', 'common']) })
// W-137 contract: the runtime capability and binding state are server decisions; only `id` identifies a template and `code` is display data.
export const templateKinds = ['manual', 'event', 'event-direct', 'unbound'] as const
export type TemplateKind = typeof templateKinds[number]
const templateReferenceSchema = z.object({ id: templateIdSchema, code: z.string().nullable() })
export type TemplateReference = z.infer<typeof templateReferenceSchema>
const triggerStateShape = {
  type: z.enum(templateKinds), isSystem: z.boolean(), bound: z.boolean(), bindable: z.boolean(),
  copyOf: templateReferenceSchema.nullable(), replacedBy: templateReferenceSchema.nullable(), systemTemplate: templateReferenceSchema.nullable(),
}
// `none` carries no reference or flag; incomplete `self`/`other` flags are an invalid upstream contract, never defaulted.
const activeFlagsShape = { id: templateIdSchema, code: z.string().nullish(), bound: z.boolean(), isSystem: z.boolean() }
const activeOnTriggerSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('none') }),
  z.object({ kind: z.literal('self'), ...activeFlagsShape }),
  z.object({ kind: z.literal('other'), ...activeFlagsShape }),
])
export type ActiveOnTrigger = z.infer<typeof activeOnTriggerSchema>
export const templateDetailSchema = z.object({
  id: templateIdSchema, name: z.string().nullable(), code: z.string().nullable(), trigger: z.string().nullable(),
  isEnabled: z.boolean().nullable(), contents: templateContentSchema.array().nullable(),
  allowedVariables: allowedVariableSchema.array().nullable().optional(),
  ...triggerStateShape, activeOnTrigger: activeOnTriggerSchema.nullable(),
}).refine(value => value.allowedVariables == null || new Set(value.allowedVariables.map(item => item.key)).size === value.allowedVariables.length)
  .refine(value => value.activeOnTrigger === null || value.activeOnTrigger.kind === 'none' ||
    (value.activeOnTrigger.kind === 'self') === (value.activeOnTrigger.id.toLowerCase() === value.id.toLowerCase()))
export const templateUsageSchema = z.object({
  items: z.object({ id: templateIdSchema, at: z.iso.datetime({ offset: true }).nullable(), toAddress: z.string().nullable(),
    channelId: templateIdSchema.nullable(), channelName: z.string().nullable(), channelType: z.string().nullable(),
    language: z.string().nullable(), status: z.string().nullable() }).array(),
  page: pageNumber, pageSize, totalCount: count, usage30Days: count, sentCount: count, permanentErrorCount: count,
  hasNextPage: z.boolean(), metricsAsOf: z.iso.datetime({ offset: true }),
}).refine(value => value.items.length <= value.pageSize && value.sentCount <= value.totalCount &&
  value.usage30Days <= value.totalCount && value.permanentErrorCount <= value.totalCount &&
  value.hasNextPage === value.page * value.pageSize < value.totalCount)
export type TemplateUsage = z.infer<typeof templateUsageSchema>
const templatePreviewInputSchema = z.object({ contentId: templateIdSchema,
  sampleValues: z.record(z.string().regex(/^[a-z][a-z0-9]*(?:\.[a-z][a-z0-9]*)+$/), z.string()).optional(),
  draftSubject: z.string().max(255).optional(), draftBody: z.string().max(65536).optional(),
})
export type TemplatePreviewInput = z.input<typeof templatePreviewInputSchema>
const templatePreviewSchema = z.object({ contentId: templateIdSchema, channelType: z.enum(channelTypes),
  language: z.string().nullable(), subject: z.string().nullable(), body: z.string() })
export type TemplatePreview = z.infer<typeof templatePreviewSchema>
export type TemplateDetail = z.infer<typeof templateDetailSchema>
export const templateContentInputSchema = z.object({
  language: z.string().trim().min(2).max(35), channelType: z.enum(['Email', 'SMS', 'PushNotification']),
  subject: z.string().transform(dotnetTrim).pipe(z.string().max(255).refine(value => !hasControlChar(value))),
  body: z.string().min(1).max(65536).refine(value => dotnetTrim(value).length > 0 &&
    !hasControlChar(value.replace(/[\n\r\t]/g, '')) && new TextEncoder().encode(value).length <= 65536),
  expectedRevision: revisionSchema,
}).refine(value => !['Email', 'PushNotification'].includes(value.channelType) || value.subject.length > 0)
export type TemplateContentInput = z.input<typeof templateContentInputSchema>
const contentSavedSchema = z.object({ templateId: templateIdSchema, contentId: templateIdSchema })
const languageSchema = z.object({ values: namedId.array(), hasUnknown: z.boolean() }).refine(value => new Set(value.values).size === value.values.length)
const channelSchema = z.object({ type: z.enum(channelTypes), languageCount: count.nullable(), hasUnknownLanguage: z.boolean() }).refine(value => (value.languageCount === null) === value.hasUnknownLanguage)
const itemSchema = z.object({
  id: z.string().regex(/^[a-f\d]{24}$/i), name: z.string().nullable(), code: z.string().nullable(), trigger: z.string().nullable(), isEnabled: z.boolean().nullable(),
  ...triggerStateShape,
  contentCount: count.nullable(), contentsState: z.enum(['known', 'unknown']), languages: languageSchema,
  channels: z.object({ items: channelSchema.array(), hasUnknown: z.boolean() }), isMultilingual: z.boolean().nullable(),
  usage30Days: count.nullable(), usage30DaysState: z.enum(['known', 'unavailable']),
}).refine(value => {
  if ((value.usage30Days === null) !== (value.usage30DaysState === 'unavailable')) return false
  if ((value.contentCount === null) !== (value.contentsState === 'unknown')) return false
  const { values, hasUnknown } = value.languages
  if (value.isMultilingual !== (values.length >= 2 ? true : hasUnknown ? null : false)) return false
  if (value.contentsState === 'unknown') return values.length === 0 && hasUnknown && value.channels.items.length === 0 && value.channels.hasUnknown
  if (values.length > value.contentCount!) return false
  if (value.contentCount === 0 && (hasUnknown || value.channels.hasUnknown || value.channels.items.length)) return false
  return value.channels.items.every((channel, index, items) =>
    (index === 0 || channelTypes.indexOf(channel.type) > channelTypes.indexOf(items[index - 1]!.type)) &&
    (channel.languageCount === null ? hasUnknown : channel.languageCount > 0 && channel.languageCount <= values.length))
})
const summarySchema = z.object({
  templateCount: count, enabledTemplateCount: count, disabledTemplateCount: count, unknownEnabledTemplateCount: count,
  contentCount: count.nullable(), contentCountState: z.enum(['known', 'unavailable']), unknownContentTemplateCount: count,
  multilingualTemplateCount: count.nullable(), multilingualCountState: z.enum(['known', 'unavailable']), knownMultilingualTemplateCount: count,
  singleLanguageTemplateCount: count, noNamedLanguageTemplateCount: count, unknownMultilingualTemplateCount: count,
  usage30Days: count.nullable(), usage30DaysState: z.enum(['known', 'unavailable']), metricsAsOf: z.iso.datetime({ offset: true }).nullable(),
}).refine(value =>
  (value.usage30Days === null) === (value.usage30DaysState === 'unavailable') &&
  (value.metricsAsOf === null) === (value.usage30DaysState === 'unavailable') &&
  value.enabledTemplateCount + value.disabledTemplateCount + value.unknownEnabledTemplateCount === value.templateCount &&
  value.knownMultilingualTemplateCount + value.singleLanguageTemplateCount + value.noNamedLanguageTemplateCount + value.unknownMultilingualTemplateCount === value.templateCount &&
  value.unknownContentTemplateCount <= value.templateCount &&
  (value.contentCount === null) === (value.unknownContentTemplateCount > 0) &&
  (value.contentCountState === 'unavailable') === (value.contentCount === null) &&
  (value.multilingualTemplateCount === null) === (value.unknownMultilingualTemplateCount > 0) &&
  (value.multilingualCountState === 'unavailable') === (value.multilingualTemplateCount === null) &&
  (value.multilingualTemplateCount === null || value.multilingualTemplateCount === value.knownMultilingualTemplateCount))
export const templatePageSchema = z.object({
  items: itemSchema.array(), page: pageNumber, pageSize, totalCount: count, totalPages: count, hasNextPage: z.boolean(), summary: summarySchema,
}).refine(value => value.items.length <= value.pageSize && new Set(value.items.map(item => item.id)).size === value.items.length &&
  value.totalPages === Math.ceil(value.totalCount / value.pageSize) && value.hasNextPage === (value.page < value.totalPages))
export type TemplatePage = z.infer<typeof templatePageSchema>
export const templateFilters = ['all', 'enabled', 'disabled', 'multilingual'] as const
const filtersSchema = z.object({ page: pageNumber, pageSize, search: z.string().trim().refine(value => value.length <= 100), filter: z.enum(templateFilters) })
export type TemplateFilters = z.infer<typeof filtersSchema>

/** Read known scalar fields only; organization and tenant hints are never sent. */
export function parseTemplateQuery(query: Record<string, unknown>) {
  const integer = (value: unknown, fallback: number) => value === undefined ? fallback : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN
  const filter = typeof query.filter === 'string' ? query.filter.trim() || 'all' : query.filter === undefined ? 'all' : query.filter
  return filtersSchema.safeParse({ page: integer(query.page, 1), pageSize: integer(query.pageSize, 25), search: query.search === undefined ? '' : query.search, filter })
}
export function templateRouteQuery(query: Record<string, unknown>) {
  const parsed = parseTemplateQuery(query)
  const value = parsed.success ? parsed.data : { page: 1, pageSize: 25, search: '', filter: 'all' }
  return { page: String(value.page), pageSize: String(value.pageSize), ...(value.search ? { search: value.search } : {}), ...(value.filter !== 'all' ? { filter: value.filter } : {}) }
}
// 409 CONSOLE_TEMPLATE_TRIGGER_ALREADY_ACTIVE names the active template; the write response is authoritative over any earlier read.
const activeTemplateReferenceSchema = z.object({ id: templateIdSchema, code: z.string().nullable(), bound: z.boolean(), isSystem: z.boolean() })
export type ActiveTemplateReference = z.infer<typeof activeTemplateReferenceSchema>
function readActiveTemplate(body: Record<string, unknown>): ActiveTemplateReference | undefined {
  const parsed = activeTemplateReferenceSchema.safeParse(body['activeTemplate'])
  return parsed.success ? parsed.data : undefined
}
/** The active template reference carried by an ALREADY_ACTIVE conflict; undefined for any other failure. */
export function conflictActiveTemplate(error: unknown): ActiveTemplateReference | undefined {
  return error instanceof ApiError && error.status === 409 && error.code === 'CONSOLE_TEMPLATE_TRIGGER_ALREADY_ACTIVE'
    ? activeTemplateReferenceSchema.safeParse(error.details).data : undefined
}
export function createTemplatesApi(client: ApiClient) {
  const headers = { 'X-Console-Request': '1' }
  const base = '/api/v1/console/communication/templates'
  function contentPath(id: string, contentId: string) { return `${base}/${templateIdSchema.parse(id)}/contents/${templateIdSchema.parse(contentId)}` }
  function assertContentBinding(value: unknown, id: string, contentId?: string) {
    const response = parseResponse(contentCreatedSchema, value)
    if (response.templateId.toLowerCase() !== id.toLowerCase() || contentId && response.contentId.toLowerCase() !== contentId.toLowerCase())
      throw new ApiError('Invalid content mutation scope', 200, 'INVALID_RESPONSE')
    return response
  }
  return {
    async create(input: TemplateCreateInput, signal?: AbortSignal) {
      const body = templateCreateSchema.parse(input)
      return parseResponse(templateCreatedSchema, await client.request<unknown>('/api/v1/console/communication/templates', {
        method: 'POST', body, headers: { 'X-Console-Request': '1' }, signal,
      }))
    },
    async detail(id: string, signal?: AbortSignal) {
      const input = templateIdSchema.parse(id)
      const value = parseResponse(templateDetailSchema, await client.request<unknown>(`/api/v1/console/communication/templates/${input}`, { signal }))
      if (value.id.toLowerCase() !== input.toLowerCase()) throw new ApiError('Invalid template detail', 200, 'INVALID_RESPONSE')
      return value
    },
    async usage(id: string, page = 1, signal?: AbortSignal): Promise<TemplateUsage> {
      const template = templateIdSchema.parse(id), requestedPage = pageNumber.parse(page)
      const value = parseResponse(templateUsageSchema, await client.request<unknown>(`/api/v1/console/communication/templates/${template}/usage?page=${requestedPage}&pageSize=40`, { signal }))
      if (value.page !== requestedPage || value.pageSize !== 40) throw new ApiError('Invalid template usage page', 200, 'INVALID_RESPONSE')
      return value
    },
    async preview(id: string, input: TemplatePreviewInput, signal?: AbortSignal): Promise<TemplatePreview> {
      const template = templateIdSchema.parse(id), body = templatePreviewInputSchema.parse(input)
      const value = parseResponse(templatePreviewSchema, await client.request<unknown>(`/api/v1/console/communication/templates/${template}/preview`, {
        method: 'POST', body, headers: { 'X-Console-Request': '1' }, signal,
      }))
      if (value.contentId.toLowerCase() !== body.contentId.toLowerCase()) throw new ApiError('Invalid template preview content', 200, 'INVALID_RESPONSE')
      return value
    },
    async createContent(id: string, input: z.input<typeof contentCreateInputSchema>, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), body = contentCreateInputSchema.parse(input)
      return assertContentBinding(await client.request<unknown>(`${base}/${template}/contents`, { method: 'POST', body, headers, signal }), template)
    },
    async setContentStatus(id: string, contentId: string, input: z.input<typeof contentStatusInputSchema>, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), content = templateIdSchema.parse(contentId), body = contentStatusInputSchema.parse(input)
      return assertContentBinding(await client.request<unknown>(`${contentPath(template, content)}/status`, { method: 'PATCH', body, headers, signal }), template, content)
    },
    async setDefaultContent(id: string, contentId: string, input: z.input<typeof revisionInputSchema>, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), content = templateIdSchema.parse(contentId), body = revisionInputSchema.parse(input)
      return assertContentBinding(await client.request<unknown>(`${contentPath(template, content)}/default`, { method: 'PATCH', body, headers, signal }), template, content)
    },
    async deleteContent(id: string, contentId: string, input: z.input<typeof revisionInputSchema>, signal?: AbortSignal) {
      const body = revisionInputSchema.parse(input)
      const value = await client.request<unknown>(contentPath(id, contentId), { method: 'DELETE', body, headers, signal })
      if (value !== undefined) throw new ApiError('Invalid deleted content response', 200, 'INVALID_RESPONSE')
    },
    async setTemplateStatus(id: string, input: z.input<typeof templateStatusInputSchema>, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), body = templateStatusInputSchema.parse(input)
      const value = parseResponse(templateStatusResultSchema, await client.request<unknown>(`${base}/${template}/status`, { method: 'PATCH', body, headers, signal, errorDetails: readActiveTemplate }))
      if (value.templateId.toLowerCase() !== template.toLowerCase()) throw new ApiError('Invalid template status scope', 200, 'INVALID_RESPONSE')
      return value
    },
    /** One server transaction: closes the previous active template and activates `id` (or restores a system seed). Idempotent. */
    async bindTrigger(id: string, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id)
      const value = parseResponse(templateDetailSchema, await client.request<unknown>(`${base}/${template}/bind-trigger`, { method: 'POST', body: {}, headers, signal }))
      if (value.id.toLowerCase() !== template.toLowerCase()) throw new ApiError('Invalid bind-trigger scope', 200, 'INVALID_RESPONSE')
      return value
    },
    async deleteTemplate(id: string, input: z.input<typeof deleteTemplateInputSchema>, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), body = deleteTemplateInputSchema.parse(input)
      const value = await client.request<unknown>(`${base}/${template}`, { method: 'DELETE', body, headers, signal })
      if (value !== undefined) throw new ApiError('Invalid deleted template response', 200, 'INVALID_RESPONSE')
    },
    async testSend(id: string, input: z.input<typeof templateTestSendInputSchema>, key: string, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), body = templateTestSendInputSchema.parse(input), operationKey = templateTestKeySchema.parse(key)
      return parseResponse(testSendPostResultSchema, await client.request<unknown>(`${base}/${template}/test-send`, {
        method: 'POST', body, headers: { ...headers, 'Idempotency-Key': operationKey }, signal,
      }))
    },
    async testSendStatus(id: string, key: string, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), operationKey = templateTestKeySchema.parse(key)
      return parseResponse(testSendStatusSchema, await client.request<unknown>(`${base}/${template}/test-send/${operationKey}`, { signal }))
    },
    async saveContent(id: string, contentId: string, input: TemplateContentInput, signal?: AbortSignal) {
      const template = templateIdSchema.parse(id), content = templateIdSchema.parse(contentId), body = templateContentInputSchema.parse(input)
      const value = parseResponse(contentSavedSchema, await client.request<unknown>(`/api/v1/console/communication/templates/${template}/contents/${content}`, {
        method: 'PUT', body, headers: { 'X-Console-Request': '1' }, signal,
      }))
      if (value.templateId.toLowerCase() !== template.toLowerCase() || value.contentId.toLowerCase() !== content.toLowerCase())
        throw new ApiError('Invalid saved content', 200, 'INVALID_RESPONSE')
      return value
    },
    async list(filters: TemplateFilters, signal?: AbortSignal) {
      const input = filtersSchema.parse(filters), query = new URLSearchParams()
      for (const [key, value] of Object.entries(input)) query.set(key, String(value))
      const value = parseResponse(templatePageSchema, await client.request<unknown>(`/api/v1/console/communication/templates?${query}`, { signal }))
      if (value.page !== input.page || value.pageSize !== input.pageSize) throw new ApiError('Invalid template page', 200, 'INVALID_RESPONSE')
      return value
    },
  }
}
export function templateCreateErrorKey(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'unauthorized'
    if (error.status === 403) return 'forbidden'
    if (error.code === 'TEMPLATE_CODE_ALREADY_EXISTS') return 'duplicateCode'
    if (error.code === 'CONSOLE_TEMPLATE_SOURCE_INVALID') return 'sourceInvalid'
    if (error.status === 404 && error.code === 'CONSOLE_TEMPLATE_NOT_FOUND') return 'sourceInvalid'
    if (error.code === 'CONSOLE_TEMPLATE_CONTENT_TOO_LARGE') return 'tooLarge'
    if (error.code === 'CONSOLE_TEMPLATE_TRIGGER_BINDING_LIMIT_EXCEEDED') return 'bindingLimit'
    if (error.code === 'CONSOLE_TEMPLATE_OUTCOME_UNCERTAIN') return 'uncertain'
    if (error.code === 'CONSOLE_TEMPLATE_INPUT_INVALID') return 'invalid'
    if (error.code === 'INVALID_RESPONSE') return 'invalidResponse'
  }
  return 'unavailable'
}
/**
 * W-137 rejects enabling or binding an automatic template, or a copy, whose trigger has no producer. Working code name: the backend
 * (#300) confirms the final one; an unknown code keeps the safe `unavailable` fallback.
 */
export const templateTriggerUnboundCode = 'CONSOLE_TEMPLATE_TRIGGER_UNBOUND'
export function templateSaveErrorKey(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'unauthorized'
    if (error.status === 403) return 'forbidden'
    if (error.code === 'CONSOLE_TEMPLATE_CONTENT_CONFLICT') return 'conflict'
    if (error.code === 'CONSOLE_TEMPLATE_ACTIVE') return 'active'
    // W-137 trigger rules: every code gets its own sentence; none falls into the generic unavailable text.
    if (error.code === 'CONSOLE_TEMPLATE_SYSTEM_LOCKED') return 'systemLocked'
    if (error.code === 'CONSOLE_TEMPLATE_BOUND_LOCKED') return 'boundLocked'
    if (error.code === 'CONSOLE_TEMPLATE_BIND_REQUIRED') return 'bindRequired'
    if (error.code === 'CONSOLE_TEMPLATE_TRIGGER_NOT_BINDABLE') return 'notBindable'
    if (error.code === 'CONSOLE_TEMPLATE_TRIGGER_BINDING_LIMIT_EXCEEDED') return 'bindingLimit'
    if (error.code === 'CONSOLE_TEMPLATE_TRIGGER_ALREADY_ACTIVE') return 'alreadyActive'
    if (error.code === 'CONSOLE_TEMPLATE_NO_ENABLED_CONTENT') return 'noEnabledContent'
    if (error.code === templateTriggerUnboundCode) return 'triggerUnbound'
    if (error.code === 'CONSOLE_TEMPLATE_SOURCE_INVALID') return 'sourceInvalid'
    if (error.status === 404 && error.code === 'CONSOLE_TEMPLATE_CONTENT_NOT_FOUND') return 'contentNotFound'
    if (error.status === 404 && error.code === 'CONSOLE_TEMPLATE_NOT_FOUND') return 'notFound'
    if (error.code === 'CONSOLE_TEMPLATE_CONTENT_TOO_LARGE') return 'tooLarge'
    if (error.code === 'CONSOLE_TEMPLATE_OUTCOME_UNCERTAIN') return 'uncertain'
    if (error.code === 'CONSOLE_TEMPLATE_CONTENT_INVALID') return 'invalid'
    if (error.code === 'CONSOLE_TEMPLATE_CONTENT_SYNTAX_INVALID') return 'syntaxInvalid'
    if (error.code === 'INVALID_RESPONSE') return 'invalidResponse'
  }
  return 'unavailable'
}
/**
 * Failure of the bind-trigger write (bind and return-to-system). The content-editor sentences ("your text is kept", "details could not be
 * loaded") do not describe a lifecycle write, so they are replaced; whatever cannot be told apart is an unverified outcome and the page re-reads.
 */
export function templateTriggerErrorKey(error: unknown) {
  const key = templateSaveErrorKey(error)
  switch (key) {
    case 'uncertain': return 'triggerUncertain'
    case 'unavailable': case 'invalidResponse': return 'triggerUnverified'
    case 'forbidden': return 'triggerForbidden'
    case 'conflict': case 'active': case 'invalid': case 'syntaxInvalid': case 'tooLarge': case 'contentNotFound': case 'sourceInvalid': return 'triggerFailed'
    default: return key
  }
}
export function templateDetailErrorKey(error: unknown) {
  if (error instanceof ApiError && error.status === 404) return 'notFound'
  const key = templateErrorKey(error)
  return key === 'invalidQuery' ? 'invalidResponse' : key
}
export function useTemplateCatalog(filters: ComputedRef<TemplateFilters | undefined>) {
  const session = useCurrentSession(), api = createTemplatesApi(useApiClient())
  const principal = computed(() => session.state.status === 'authenticated' && session.state.session?.roles.includes('System_Admin') ? session.state.session.userId : undefined)
  const query = useQuery({
    queryKey: computed(() => ['console', principal.value, 'template-catalog', filters.value]),
    queryFn: ({ signal }) => api.list(filters.value!, signal), enabled: computed(() => Boolean(principal.value && filters.value)),
    placeholderData: (previous, previousQuery) => samePageScope(previousQuery?.queryKey, ['console', principal.value, 'template-catalog', filters.value]) ? previous : undefined,
    retry: false, refetchOnWindowFocus: false,
  })
  const data = computed(() => principal.value && filters.value && !query.error.value ? query.data.value : undefined)
  return { query, data, principal }
}
export function templateErrorKey(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'unauthorized'
    if (error.status === 403) return 'forbidden'
    if (error.code === 'CONSOLE_TEMPLATE_QUERY_INVALID') return 'invalidQuery'
    if (error.code === 'INVALID_RESPONSE') return 'invalidResponse'
  }
  return 'unavailable'
}
